import { LIVESTOCK_SPECIES, RDLivestock } from '@cibo/core'
import { NumberInputField, RadioBooleanField, SelectField } from '@cibo/ui'
import { Collapse, FormControl, FormHelperText, FormLabel, Stack, Typography } from '@mui/material'
import { Field, FieldProps } from 'formik'
import { useTranslation } from 'react-i18next'

import { DetailEditorPropsByYear } from '../types'

export const LivestockEditor = ({ year, name = 'livestock' }: DetailEditorPropsByYear) => {
  const { t } = useTranslation('@cibo/landmanager/LivestockEditor')

  return (
    <Field name={name} id={name}>
      {(props: FieldProps<RDLivestock['input'] & { livestockPresent: 'true' | 'false' }>) => {
        const {
          field: { value },
        } = props

        // these errors are present, but the formik typing is not aware of them
        // @ts-ignore
        const daysGrazingError = props.meta.error?.daysGrazing
        // @ts-ignore
        const speciesError = props.meta.error?.species
        // @ts-ignore
        const headcountError = props.meta.error?.headcount

        return (
          <Stack spacing={2}>
            <Typography>{t('cta', { year })}</Typography>
            <FormControl>
              <FormLabel htmlFor={`${name}.livestockPresent`} required>
                {t('didYouGraze')}
              </FormLabel>
              <RadioBooleanField name={`${name}.livestockPresent`} />
            </FormControl>

            <Collapse in={value?.livestockPresent === 'true'}>
              <Stack spacing={2}>
                <FormControl error={!!speciesError}>
                  <FormLabel htmlFor={`${name}.species`}>
                    <Typography>{t('whatType')}</Typography>
                  </FormLabel>

                  <SelectField
                    error={!!speciesError}
                    fieldName={`${name}.species`}
                    options={LIVESTOCK_SPECIES}
                    renderOption={context =>
                      t('species', { context, ns: '@cibo/landmanager/LivestockSpecies' })
                    }
                  />
                  {!!speciesError && <FormHelperText>{speciesError}</FormHelperText>}
                </FormControl>

                <FormControl error={!!headcountError}>
                  <FormLabel htmlFor={`${name}.headcount`}>{t('howMany')}</FormLabel>
                  <NumberInputField
                    error={!!headcountError}
                    name={`${name}.headcount`}
                    min={1}
                    step={1}
                    data-testid={`${name}.headcount`}
                  />
                  {!!headcountError && <FormHelperText>{headcountError}</FormHelperText>}
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor={`${name}.daysGrazing`}>{t('howManyDays')}</FormLabel>
                  <NumberInputField
                    error={!!daysGrazingError}
                    helperText={!!daysGrazingError && daysGrazingError}
                    name={`${name}.daysGrazing`}
                    min={1}
                    max={365}
                    step={1}
                    data-testid={`${name}.daysGrazing`}
                  />
                  {!!daysGrazingError && <FormHelperText>{daysGrazingError}</FormHelperText>}
                </FormControl>
              </Stack>
            </Collapse>
          </Stack>
        )
      }}
    </Field>
  )
}
